import React, { useEffect, useRef, useState } from "react";
import Section from "./Section";
import { X_PADDING } from "../constants";
import Page from "./Page";

const MemberInsurance = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const sectionRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseEnter = (id) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredId(id);
    }, 100);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredId(null);
  };

  return (
    <Page name="Member Health Insurance">
      <div className="w-full pt-8 bg-white">
        {/* Member Benefits Section */}
        <Section
          name="Member Benefits"
          description="Become part of a community that is shaping the future of healthcare in Zambia.
           As a COAZ member, you'll enjoy exclusive benefits, professional development opportunities, 
           and resources that empower you to grow, connect, and make a lasting impact on the clinical profession."
        />

        {/* Benefits Grid - 3 rows, 4 columns with slide-in from left */}
        <div ref={sectionRef} className={`${X_PADDING} w-full mx-auto py-8`}>
          {/* Row 1 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {BENEFITS_ROW1.map((benefit, index) => {
              const cardId = `benefit-row1-${index}`;
              const isExpanded = hoveredId === cardId;
              const isShrunk = hoveredId !== null && hoveredId !== cardId;

              return (
                <div
                  key={cardId}
                  className={`
                    transform transition-all duration-700 ease-out
                    ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'}
                  `}
                  style={{ 
                    transitionDelay: `${index * 100}ms`,
                    transitionProperty: 'all'
                  }}
                >
                  <BenefitCard
                    benefit={benefit}
                    isExpanded={isExpanded}
                    isShrunk={isShrunk}
                    onMouseEnter={() => handleMouseEnter(cardId)}
                    onMouseLeave={handleMouseLeave}
                  />
                </div>
              );
            })}
          </div>

          {/* Row 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {BENEFITS_ROW2.map((benefit, index) => {
              const cardId = `benefit-row2-${index}`;
              const isExpanded = hoveredId === cardId;
              const isShrunk = hoveredId !== null && hoveredId !== cardId;

              return (
                <div
                  key={cardId}
                  className={`
                    transform transition-all duration-700 ease-out
                    ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'}
                  `}
                  style={{ 
                    transitionDelay: `${(index + BENEFITS_ROW1.length) * 100}ms`,
                    transitionProperty: 'all'
                  }}
                >
                  <BenefitCard
                    benefit={benefit}
                    isExpanded={isExpanded}
                    isShrunk={isShrunk}
                    onMouseEnter={() => handleMouseEnter(cardId)}
                    onMouseLeave={handleMouseLeave}
                  />
                </div>
              );
            })}
          </div>

          {/* Row 3 - New Benefits from points 4, 5, 6, and 10 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS_ROW3.map((benefit, index) => {
              const cardId = `benefit-row3-${index}`;
              const isExpanded = hoveredId === cardId;
              const isShrunk = hoveredId !== null && hoveredId !== cardId;

              return (
                <div
                  key={cardId}
                  className={`
                    transform transition-all duration-700 ease-out
                    ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'}
                  `}
                  style={{ 
                    transitionDelay: `${(index + BENEFITS_ROW1.length + BENEFITS_ROW2.length) * 100}ms`,
                    transitionProperty: 'all'
                  }}
                >
                  <BenefitCard
                    benefit={benefit}
                    isExpanded={isExpanded}
                    isShrunk={isShrunk}
                    onMouseEnter={() => handleMouseEnter(cardId)}
                    onMouseLeave={handleMouseLeave}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Why Join COAZ Banner - Full Width Blue Section with Text */}
        <div className="relative w-full overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url(/images/img_46.jpg)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1566ad] via-[#1b9de3] to-[#1b9de3]/80" />
          <div className="relative mx-auto w-full px-6 py-16 lg:px-16">
            <div className="max-w-4xl">
              <h2 className="mb-6 text-3xl font-bold text-white">
                Why Join COAZ?
              </h2>
              <p className="text-white/90 leading-8 mb-4 text-base">
                As a member of the Clinical Officers Association of Zambia
                (COAZ), you join a unified network committed to advancing the
                clinical profession and strengthening healthcare delivery
                nationwide.
              </p>
              <p className="text-white/90 leading-8 text-base">
                Together, we advocate for the interests of Clinical Officers,
                address challenges affecting healthcare delivery, and develop
                the next generation of healthcare leaders in Zambia.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default MemberInsurance;

// Benefit Card Component
const BenefitCard = ({ benefit, isExpanded, isShrunk, onMouseEnter, onMouseLeave }) => {
  return (
    <div
      className={`
        rounded-xl shadow-md hover:shadow-xl transition-all duration-500 ease-in-out
        overflow-hidden group relative
        ${isExpanded ? 'scale-110 shadow-2xl z-20' : ''}
        ${isShrunk ? 'scale-95 opacity-50' : ''}
      `}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Animated Border */}
      <div className={`
        absolute inset-0 rounded-xl pointer-events-none
        transition-all duration-700 ease-in-out
        ${isExpanded ? 'border-[4px] border-[#1477c2]' : 'border-[4px] border-transparent'}
      `}>
        <div className={`
          absolute top-0 left-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out
          ${isExpanded ? 'w-full' : 'w-0'}
        `}></div>
        <div className={`
          absolute top-0 left-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-100
          ${isExpanded ? 'h-full' : 'h-0'}
        `}></div>
        <div className={`
          absolute bottom-0 right-0 w-0 h-0.5 bg-[#1477c2] transition-all duration-700 ease-in-out delay-200
          ${isExpanded ? 'w-full' : 'w-0'}
        `}></div>
        <div className={`
          absolute bottom-0 right-0 w-0.5 h-0 bg-[#1477c2] transition-all duration-700 ease-in-out delay-300
          ${isExpanded ? 'h-full' : 'h-0'}
        `}></div>
      </div>

      <div className={`h-28 flex flex-col items-center justify-center ${benefit.color} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300"></div>
        <img 
          src={benefit.image} 
          alt={benefit.title}
          className={`h-14 w-14 object-cover rounded-full border-2 border-white/30 relative z-10 transition-all duration-500 ${isExpanded ? 'h-20 w-20' : 'group-hover:scale-110'}`}
        />
        <div className="absolute -right-8 -top-8 w-24 h-24 bg-white/10 rounded-full"></div>
        <div className="absolute -left-8 -bottom-8 w-20 h-20 bg-white/5 rounded-full"></div>
      </div>
      <div className="p-4 bg-white border-x border-b border-[#eef1f4] rounded-b-xl">
        <p className={`font-bold text-sm text-[#16365c] text-center transition-all duration-500 leading-tight min-h-[40px] ${isExpanded ? 'text-[#1477c2] text-base' : ''}`}>
          {benefit.title}
        </p>
        <p className={`text-xs text-gray-400 text-center mt-1 leading-relaxed transition-all duration-500 ${isExpanded ? 'text-[#16365c]' : ''}`}>
          {benefit.description}
        </p>
      </div>
    </div>
  );
};

// Row 1 Benefits Data
const BENEFITS_ROW1 = [
  {
    title: "High Quality Lab Coats",
    description: "Access to high-quality lab coats distributed by COAZ to members",
    image: "/images/img_67.png",
    color: "bg-gradient-to-br from-[#1477c2] to-[#1b9de3]",
  },
  {
    title: "Advocacy & Representation",
    description: "Strong advocacy on issues affecting the clinical profession",
    image: "/images/img_56.png",
    color: "bg-gradient-to-br from-[#16365c] to-[#1477c2]",
  },
  {
    title: "Legal Representation",
    description: "Access to professional legal support through Legal@COAZ.org",
    image: "/images/img_65.png",
    color: "bg-gradient-to-br from-[#1b9de3] to-[#0d5a8a]",
  },
  {
    title: "Continuing Professional Development",
    description: "Resources for personal and professional development",
    image: "/images/img_62.png",
    color: "bg-gradient-to-br from-[#0d5a8a] to-[#16365c]",
  },
];

// Row 2 Benefits Data
const BENEFITS_ROW2 = [
  {
    title: "Loan & Financial Services",
    description: "Exclusive financial services and loan facilities (Coming Soon)",
    image: "/images/img_58.png",
    color: "bg-gradient-to-br from-[#1477c2] to-[#0d5a8a]",
  },
  {
    title: "Recognition & Awards",
    description: "Recognition for outstanding contributions to healthcare",
    image: "/images/img_57.png",
    color: "bg-gradient-to-br from-[#16365c] to-[#1b9de3]",
  },
  {
    title: "Networking Opportunities",
    description: "Connect with fellow health professionals across Zambia",
    image: "/images/img_59.png",
    color: "bg-gradient-to-br from-[#1b9de3] to-[#1477c2]",
  },
  {
    title: "Mentorship Programs",
    description: "Career guidance through structured mentorship programs",
    image: "/images/img_60.png",
    color: "bg-gradient-to-br from-[#0d5a8a] to-[#16365c]",
  },
];

// Row 3 Benefits Data - From Member Benefits points 4, 5, 6, and 10
const BENEFITS_ROW3 = [
  {
    title: "Influence Healthcare Policy",
    description: "Shape the future of healthcare in Zambia through advocacy",
    image: "/images/img.png",
    color: "bg-gradient-to-br from-[#1477c2] to-[#1b9de3]",
  },
  {
    title: "Continuing Education (CPD)",
    description: "Access to CPD programs and professional development workshops",
    image: "/images/img.png",
    color: "bg-gradient-to-br from-[#16365c] to-[#1477c2]",
  },
  {
    title: "Training & Workshops",
    description: "Exclusive access to training programs and skill-building workshops",
    image: "/images/img.png",
    color: "bg-gradient-to-br from-[#1b9de3] to-[#0d5a8a]",
  },
  {
    title: "Community Outreach",
    description: "Participate in health promotion and community engagement activities",
    image: "/images/img.png",
    color: "bg-gradient-to-br from-[#0d5a8a] to-[#16365c]",
  },
];